
import { LucideIcon, CheckCircle } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface RadioCardOption {
  id: string
  label: string
  icon: LucideIcon
  description?: string
}

export interface RadioCardsProps {
  name: string
  value: string | undefined
  onChange: (value: string) => void
  options: RadioCardOption[]
  disabled?: boolean
  className?: string
  columns?: {
    default: number
    sm?: number
    md?: number
    lg?: number
  }
}

export function RadioCards({
  name,
  value,
  onChange,
  options,
  disabled = false,
  className,
  columns = { default: 2, sm: 4 }
}: RadioCardsProps) {
  const gridClasses = cn(
    'grid gap-3',
    `grid-cols-${columns.default}`,
    columns.sm && `sm:grid-cols-${columns.sm}`,
    columns.md && `md:grid-cols-${columns.md}`,
    columns.lg && `lg:grid-cols-${columns.lg}`
  )

  return (
    <div className={cn(gridClasses, className)}>
      {options.map((option) => {
        const OptionIcon = option.icon
        const isSelected = value === option.id
        const hasDescription = Boolean(option.description)
        
        return (
          <label
            key={option.id}
            className={cn(
              'relative flex border rounded-md cursor-pointer transition-all',
              hasDescription ? 'flex-row items-start p-4' : 'flex-col items-center p-2',
              isSelected
                ? 'border-primary bg-primary/5 dark:bg-primary/10 hover:bg-primary/15 dark:hover:bg-primary/20'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.id}
              checked={isSelected}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="sr-only"
            />
            {hasDescription ? (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <OptionIcon
                        className={cn(
                          'h-4 w-4',
                          isSelected
                            ? 'text-primary'
                            : 'text-gray-500 dark:text-gray-400'
                        )}
                      />
                      <span>{option.label}</span>
                    </span>
                    <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {option.description}
                    </span>
                  </span>
                </span>
                {isSelected && (
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                )}
              </>
            ) : (
              <>
                {isSelected && (
                  <CheckCircle className="absolute top-2 right-2 h-4 w-4 text-primary" />
                )}
                <OptionIcon
                  className={cn(
                    'h-4 w-4 mb-1',
                    isSelected
                      ? 'text-primary'
                      : 'text-gray-500 dark:text-gray-400'
                  )}
                />
                <span
                  className={cn(
                    'text-xs font-medium',
                    isSelected
                      ? 'text-primary'
                      : 'text-gray-700 dark:text-gray-300'
                  )}
                >
                  {option.label}
                </span>
              </>
            )}
          </label>
        )
      })}
    </div>
  )
} 